import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IDefaultProps, TPublishStatus } from '../../../types';
import { triggerEtlPublish, fetchEtlStatus } from '../../../utils/api';
import { later } from '../../../utils/later';
import {
  fetchSettings,
  selectSettings,
  useAppDispatch,
  useAppSelector,
} from '../../../state';
import {
  selectFilesState,
  setPublishStatus,
} from '../../../state/slices/filesSlice';
import { NavigationWrapper } from '../NavigationWrapper';
import { PublishingProgress } from '../../../components/PublishingProgress';

interface IProps extends IDefaultProps {
  test?: boolean;
}

const PROGRESS_BY_STATUS: Record<TPublishStatus, number> = {
  None: 0,
  Queued: 10,
  Started: 50,
  Completed: 100,
  Failed: 0,
};

export const Publishing: React.FC<IProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const settingsData = useAppSelector(selectSettings);
  const { publishStatus } = useAppSelector(selectFilesState);

  useEffect(() => {
    if (
      !settingsData.settings &&
      !settingsData.loading &&
      !settingsData.failed
    ) {
      dispatch(fetchSettings());
    }
  }, []);

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { status } = await fetchEtlStatus();
        dispatch(setPublishStatus(status));
        if (status !== 'Completed' && status !== 'Failed') {
          await later(5000);
          poll();
        }
      } catch {
        dispatch(setPublishStatus('Failed'));
      }
    };

    const trigger = async () => {
      dispatch(setPublishStatus('Queued'));
      try {
        await triggerEtlPublish();
      } catch {
        dispatch(setPublishStatus('Failed'));
        return;
      }
      poll();
    };

    trigger();

    return () => {
      stopped = true;
    };
  }, []);

  useEffect(() => {
    if (publishStatus === 'Completed') {
      navigate('/data-manager/reports');
    }
  }, [publishStatus]);

  const handleCancel = () => {
    navigate(-1);
  };

  const progress = PROGRESS_BY_STATUS[publishStatus] ?? 0;
  const message =
    publishStatus === 'Failed'
      ? 'Publishing failed. Please try again.'
      : 'Mapped files are currently being published to reports';

  return (
    <>
      <NavigationWrapper
        onFileUploadClick={console.log}
        isUploadDisabled={false}
        hideStatusMessage
      />
      <PublishingProgress
        title="Updating reports"
        subtitle="Push your latest mapped data to reports. This will replace the existing version."
        progress={progress}
        onCancel={handleCancel}
        message={message}
      />
    </>
  );
};
